"use client";

import { useEffect, useState, useRef } from "react";
import clsx from "clsx";

import { FaRegPlayCircle } from "react-icons/fa";

export default function VttChapters({
  videoUrl,
  onJump,
  captionsTrack,
  currentTime,
}) {
  const [chapters, setChapters] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(false);
  const activeRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Find the active chapter index based on currentTime
  const activeIndex = chapters.reduce((acc, entry, idx) => {
    if (currentTime >= entry.time) return idx;
    return acc;
  }, -1);

  // Auto-scroll to active chapter
  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [activeIndex]);

  useEffect(() => {
    async function fetchVtt() {
      try {
        let vttUrl;
        if (captionsTrack) {
          vttUrl = videoUrl.replace(/\.m3u8$/, `/text/${captionsTrack}.vtt`);
        } else {
          vttUrl = videoUrl.replace(/vod\.m3u8$/, "captions.en.vtt");
        }
        const res = await fetch(vttUrl);
        if (!res.ok) throw new Error("Failed to fetch VTT");
        const text = await res.text();
        const parsed = parseVtt(text);
        if (parsed.length === 0) throw new Error("Empty transcript");
        setChapters(parsed);
      } catch (err) {
        console.error("Transcript load error:", err.message);
        setError(true);
      }
    }
    if (videoUrl) {
      fetchVtt();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoUrl]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [searchTerm]);

  const parseVtt = (vttText) => {
    const grouped = new Map();
    const lines = vttText.split(/\r?\n/);
    let currentTime = null;
    let currentText = "";

    for (let i = 0; i < lines.length; i++) {
      const rawLine = lines[i];
      const line = rawLine.trim(); // Trim each line before use

      if (line.includes("-->")) {
        const [start] = line.split("-->");
        currentTime = toSeconds(start.trim());
      } else if (line && currentTime != null) {
        currentText += (currentText ? " " : "") + line;
      } else if (!line && currentText && currentTime != null) {
        const groupTime = Math.floor(currentTime / 10) * 10;
        if (!grouped.has(groupTime)) {
          grouped.set(groupTime, []);
        }
        grouped.get(groupTime).push(currentText);
        currentTime = null;
        currentText = "";
      }
    }

    if (currentText && currentTime != null) {
      const groupTime = Math.floor(currentTime / 10) * 10;
      if (!grouped.has(groupTime)) {
        grouped.set(groupTime, []);
      }
      grouped.get(groupTime).push(currentText);
    }

    return Array.from(grouped.entries()).map(([time, texts]) => ({
      time: parseFloat(time),
      text: texts
        .join(" ")
        .replace(/\s+/g, " ") // Normalize multiple spaces/tabs/newlines
        .trim(), // Trim both ends
    }));
  };
  //   const parseVtt = (vttText) => {
  //     const entries = [];
  //     const lines = vttText.split(/\r?\n/);
  //     let current = null;

  //     for (let i = 0; i < lines.length; i++) {
  //       const line = lines[i].trim();

  //       if (line.includes("-->")) {
  //         const [start] = line.split("-->");
  //         current = {
  //           time: toSeconds(start.trim()),
  //           text: "",
  //         };
  //       } else if (line && current) {
  //         current.text += (current.text ? " " : "") + line;
  //       } else if (!line && current) {
  //         entries.push(current);
  //         current = null;
  //       }
  //     }

  //     if (current) entries.push(current);
  //     return entries;
  //   };

  const toSeconds = (timeString) => {
    const [h, m, s] = timeString.split(":");
    const [sec, ms] = s.split(".");
    return (
      parseInt(h) * 3600 +
      parseInt(m) * 60 +
      parseInt(sec) +
      (parseFloat("0." + ms) || 0)
    );
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    return hrs > 0
      ? `${pad(hrs)}:${pad(mins)}:${pad(secs)}`
      : `${pad(mins)}:${pad(secs)}`;
  };

  const pad = (num) => String(num).padStart(2, "0");

  const filteredChapters = chapters.filter((entry) =>
    entry.text.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="space-y-4">
      {error ? (
        <p className="text-white 2xl:text-xl font-medium text-center mt-8">
          No Transcript Available for this Video
        </p>
      ) : (
        <div
          ref={scrollContainerRef}
          className="max-h-[60vh] overflow-y-auto pt-1.5"
        >
          <input
            type="text"
            placeholder="Search Transcript..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-zinc-500 rounded-md mt-2 bg-slate-900 text-white sticky top-0"
          />

          <div className="space-y-2 mt-2">
            {filteredChapters.map((entry, idx) => {
              const isActive = chapters[activeIndex] === entry;
              return (
                <button
                  key={idx}
                  ref={isActive ? activeRef : null}
                  onClick={() => onJump?.(entry.time)}
                  className={clsx(
                    "w-full flex text-left items-start space-x-4 px-4 py-2 rounded scroll-mt-20",
                    isActive
                      ? "bg-slate-600 ring-1 ring-zinc-400"
                      : "bg-slate-900 hover:bg-slate-700",
                  )}
                >
                  <FaRegPlayCircle className="h-3 w-3 mt-0.5 text-zinc-200 flex-shrink-0" />
                  <span className="font-mono text-sm text-zinc-100 whitespace-nowrap">
                    {formatTime(entry.time)}
                  </span>
                  <span className="text-sm text-white">{entry.text}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
